import type { InternalRequest } from '../client.js';
import type { Rule } from '@trackizy/types';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

export function rulesEndpoints(req: InternalRequest) {
  return {
    list: () => req.request<Rule[]>('/rules'),
    get: (id: string) => req.request<Rule>(`/rules/${id}`),
    create: (data: Partial<Rule>) =>
      req.request<Rule>('/rules', { method: 'POST', body: JSON.stringify(data) }),
    /** Upsert by POST — matches trackizy.js notificationRules.upsert. */
    upsert: (data: Partial<Rule>) =>
      req.request<Rule>('/rules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Rule>) =>
      req.request<Rule>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => req.request<void>(`/rules/${id}`, { method: 'DELETE' }),
    toggle: (id: string, enabled: boolean) =>
      req.request<Rule>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
    alerts: {
      history: (params?: Params) =>
        req.request<unknown[]>(`/rules/alerts/history${qsFrom(params)}`),
      acknowledge: (id: string) =>
        req.request<unknown>(`/rules/alerts/${id}/acknowledge`, { method: 'POST' }),
    },
    events: Object.assign(
      /** Callable form — accepts { deviceId, ruleId, from, to, limit, acknowledged, category }. */
      (params: {
        deviceId?: number;
        ruleId?: string;
        from?: string;
        to?: string;
        limit?: number;
        offset?: number;
        acknowledged?: boolean;
        category?: string; // comma-joined: 'speed,geofence,maint,sos,other'
      } = {}) => {
        const qs = new URLSearchParams();
        if (params.deviceId != null) qs.set('deviceId', String(params.deviceId));
        if (params.ruleId) qs.set('ruleId', params.ruleId);
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        if (params.limit != null) qs.set('limit', String(params.limit));
        if (params.offset != null) qs.set('offset', String(params.offset));
        if (params.acknowledged != null) qs.set('acknowledged', String(params.acknowledged));
        if (params.category) qs.set('category', params.category);
        const suffix = qs.toString() ? `?${qs}` : '';
        return req.request<unknown[]>(`/rules/events${suffix}`);
      },
      {
        list: (params?: Params) =>
          req.request<unknown[]>(`/rules/events${qsFrom(params)}`),
        /** Bulk ack — legacy POST /events/acknowledge with body { ids }. */
        acknowledge: (ids: string[]) =>
          req.request<unknown>('/rules/events/acknowledge', {
            method: 'POST',
            body: JSON.stringify({ ids }),
          }),
        /** Single ack — idempotent PATCH /events/:id/acknowledge. */
        acknowledgeOne: (id: string) =>
          req.request<{ id: string; acknowledged_at: string; acknowledged_by: string }>(
            `/rules/events/${id}/acknowledge`,
            { method: 'PATCH' },
          ),
        /** Mark all read — POST /events/acknowledge-all (30-day inbox window). */
        acknowledgeAll: () =>
          req.request<{ count: number }>('/rules/events/acknowledge-all', { method: 'POST' }),
        /** Tab badge source — GET /events/unread-count. */
        unreadCount: () =>
          req.request<{ count: number }>('/rules/events/unread-count'),
        loadMore: (params?: Params) =>
          req.request<unknown[]>(`/rules/events${qsFrom(params)}`),
      }
    ),
  };
}
